import React, { useState, useMemo, useCallback } from 'react';
import { ChevronDownIcon, ChevronRightIcon, FolderIcon, MagnifyingGlassIcon, MinusIcon, PlusIcon, DocumentTextIcon, RectangleStackIcon } from '@heroicons/react/20/solid';
import YeniKategoriModal from './YeniKategoriModal.js';
import { turkishToLower } from '../../utils/stringUtils';
import { toast } from 'react-toastify';
import KategoriContextMenu from './KategoriContextMenu.js';
import * as kategoriService from '../../services/kategoriService';
import { hesaplaKategoriSablonSayilari } from '../../utils/kategoriSablonSayaci';
import { useConfirm } from '../../contexts/ConfirmContext';

const KategoriAgaci = ({ kategoriler, setKategoriler, secilenKategoriler, setSecilenKategoriler, goruntulenensablonlar = [], aramaMetni, setAramaMetni, secilenEtiketler, secilenKanunMaddeleri }) => {
  const [acikKategoriler, setAcikKategoriler] = useState({});
  const [modalAcik, setModalAcik] = useState(false);
  const [yeniKategoriParent, setYeniKategoriParent] = useState(null); // alt-kategori eklerken üst kategori
  const [duzenlenecekKategori, setDuzenlenecekKategori] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [parentId, setParentId] = useState(null);
  const confirm = useConfirm();

  // Ağaç varsayılan AÇIK: yalnız açıkça false yapılan kategoriler kapalıdır.
  const toggleKategori = (kategori) => {
    setAcikKategoriler(prev => ({ ...prev, [kategori]: prev[kategori] === false ? true : false }));
  };

  const tumAltKategorileriGetir = (kategori) => {
    let altKategoriler = [kategori.isim];
    if (kategori.altKategoriler) {
      kategori.altKategoriler.forEach(alt => {
        altKategoriler = altKategoriler.concat(tumAltKategorileriGetir(alt));
      });
    }
    return altKategoriler;
  };

  // Boş seçim = "hepsi". Kategori (veya "Kategorisiz") işaretle/kaldır;
  // bir kategori işaretlenince tüm alt kategorileri de birlikte seçilir.
  const handleKategoriSecim = (kategori, altKategoriler) => {
    setSecilenKategoriler(prev => {
      const tumAltKategoriler = tumAltKategorileriGetir({ isim: kategori, altKategoriler });

      if (prev.includes(kategori)) {
        // Seçimi kaldır: kategori + tüm alt kategorileri
        return prev.filter(k => !tumAltKategoriler.includes(k));
      }

      // Seç: kategori + tüm alt kategorilerini ekle
      const yeniSecim = [...prev];
      tumAltKategoriler.forEach(k => {
        if (!yeniSecim.includes(k)) yeniSecim.push(k);
      });
      return yeniSecim;
    });
  };

  const handleKategoriSil = async (kategori) => {
    // Yanlışlıkla silmeyi önlemek için onay iste; alt kategorileri varsa kaçının
    // birlikte gideceğini uyar. (Silme tüm alt ağacı kapsar.)
    const altKategoriSayisi = tumAltKategorileriGetir(kategori).length - 1;
    const onaylandi = await confirm({
      title: altKategoriSayisi > 0 ? 'Kategoriyi ve alt kategorilerini sil' : 'Kategoriyi sil',
      message: altKategoriSayisi > 0
        ? `"${kategori.isim}" ve içindeki ${altKategoriSayisi} alt kategori kalıcı olarak silinecek. Bu işlem geri alınamaz.`
        : `"${kategori.isim}" kalıcı olarak silinecek. Bu işlem geri alınamaz.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      tone: 'danger',
    });
    if (!onaylandi) {
      return;
    }

    try {
      if (!kategori.id) {
        throw new Error(`"${kategori.isim}" kategorisi için ID bulunamadı.`);
      }
      const silmeBasarili = await kategoriService.deleteKategori(kategori.id);
      if (!silmeBasarili) {
        throw new Error('Silme işlemi başarısız oldu');
      }
      const updatedKategoriler = await kategoriService.getAllKategoriler();
      setKategoriler(updatedKategoriler);

      const silinenler = tumAltKategorileriGetir(kategori);
      setSecilenKategoriler(prev => prev.filter(k => !silinenler.includes(k)));

      setAcikKategoriler(prev => {
        const yeni = { ...prev };
        delete yeni[kategori.isim];
        return yeni;
      });

      toast.success(`"${kategori.isim}" kategorisi başarıyla silindi.`);
    } catch (error) {
      console.error('Kategori silinirken hata:', error);
      toast.error(`Kategori silinirken bir hata oluştu: ${error.message}`);
    }
  };

  const handleKategoriDuzenle = (kategori) => {
    setDuzenlenecekKategori(kategori.isim);
    setYeniKategoriParent(null);
    setModalAcik(true);
  };

  const handleKokKategoriEkle = () => {
    setDuzenlenecekKategori(null);
    setYeniKategoriParent(null);
    setParentId(null);
    setModalAcik(true);
  };

  const handleAltKategoriEkle = (kategori) => {
    setDuzenlenecekKategori(null);
    setYeniKategoriParent(kategori);
    setParentId(kategori.id);
    setModalAcik(true);
  };

  // Modal kaydet: yeni kategori oluştur veya mevcut kategorinin ismini güncelle
  const handleKategoriKaydet = async (yeniKategoriAdi, parentIdArg, eskiKategoriAdi) => {
    try {
      if (eskiKategoriAdi) {
        // Düzenleme: yalnız ismi değiştir, mevcut üst-kategoriyi koru
        const eskiKategori = await kategoriService.getKategoriByName(eskiKategoriAdi);
        await kategoriService.updateKategori(eskiKategori.id, { isim: yeniKategoriAdi, parent_id: eskiKategori.parent_id });
      } else {
        await kategoriService.createKategori({ isim: yeniKategoriAdi, parent_id: parentIdArg });
      }
      const updatedKategoriler = await kategoriService.getAllKategoriler();
      setKategoriler(updatedKategoriler);
      return true;
    } catch (error) {
      console.error('Kategori kaydedilirken hata:', error);
      toast.error('Kategori işlemi sırasında bir hata oluştu');
      return false;
    }
  };

  const handleContextMenu = useCallback((e, kategori) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, kategori });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const sablonSayilari = useMemo(() => {
    return hesaplaKategoriSablonSayilari(
      kategoriler,
      goruntulenensablonlar,
      secilenKategoriler,
      secilenEtiketler,
      secilenKanunMaddeleri,
      aramaMetni
    );
  }, [kategoriler, goruntulenensablonlar, secilenKategoriler, secilenEtiketler, secilenKanunMaddeleri, aramaMetni]);

  const renderKategori = (kategori, altKategoriler = [], derinlik = 0, ustKategoriler = []) => {
    const ikonBoyutu = 'h-5 w-5';
    const isAramaEslesmesi = aramaMetni && turkishToLower(kategori.isim).includes(turkishToLower(aramaMetni));
    const secili = secilenKategoriler.includes(kategori.isim);
    const acik = acikKategoriler[kategori.isim] !== false; // varsayılan açık
    const [dogrudan, toplam] = sablonSayilari.get(kategori.isim) || [0, 0];

    return (
      <div key={`${ustKategoriler.join('-')}-${kategori.isim}`}>
        <div
          className="group flex items-center rounded-lg p-2 transition-colors hover:bg-slate-100"
          onContextMenu={(e) => handleContextMenu(e, kategori)}
        >
          <input
            type="checkbox"
            checked={secili}
            onChange={() => handleKategoriSecim(kategori.isim, altKategoriler)}
            className={`form-checkbox ${ikonBoyutu} mr-2 rounded text-amber-500 focus:ring-amber-400`}
          />
          <div
            className={`flex min-w-0 flex-grow items-center cursor-pointer ${
              secili ? 'font-medium text-amber-700' : isAramaEslesmesi ? 'font-medium text-emerald-600' : 'text-slate-700'
            }`}
            onClick={() => toggleKategori(kategori.isim)}
          >
            {altKategoriler.length > 0 ? (
              <button type="button" className="mr-1 shrink-0 focus:outline-none">
                {acik
                  ? <ChevronDownIcon className={`${ikonBoyutu} text-slate-400`} />
                  : <ChevronRightIcon className={`${ikonBoyutu} text-slate-400`} />}
              </button>
            ) : (
              <MinusIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-300`} />
            )}
            <FolderIcon className={`${ikonBoyutu} mr-2 shrink-0 text-amber-400`} />
            <span className="min-w-0 flex-1 truncate text-sm" title={kategori.isim}>{kategori.isim}</span>
            {toplam > 0 && (
              <span className="app-num ml-2 flex shrink-0 items-center gap-1 text-[11px] leading-none">
                <span
                  className="inline-flex items-center gap-0.5 text-slate-500"
                  title={`Bu kategoriye doğrudan atanmış ${dogrudan} şablon`}
                >
                  <DocumentTextIcon className="h-3 w-3 text-slate-400" />
                  {dogrudan}
                </span>
                {toplam !== dogrudan && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700"
                    title={`Alt kategoriler dâhil toplam ${toplam} şablon`}
                  >
                    <RectangleStackIcon className="h-3 w-3" />
                    {toplam}
                  </span>
                )}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleAltKategoriEkle(kategori); }}
            className="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 opacity-100 transition-colors hover:bg-slate-200 hover:text-amber-600 sm:opacity-0 sm:group-hover:opacity-100"
            title="Alt kategori ekle"
            aria-label={`${kategori.isim} altına alt kategori ekle`}
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
        {acik && altKategoriler.length > 0 && (
          <div className="ml-4">
            {altKategoriler.map(alt => renderKategori(alt, alt.altKategoriler || [], derinlik + 1, [...ustKategoriler, kategori.isim]))}
          </div>
        )}
      </div>
    );
  };

  const filtrelenmiKategoriler = useMemo(() => {
    if (!aramaMetni) return kategoriler;

    const filtreleme = (kategoriListesi) => {
      return kategoriListesi.reduce((acc, kategori) => {
        const isimUyuyor = turkishToLower(kategori.isim).includes(turkishToLower(aramaMetni));
        const altKategorilerSonuc = kategori.altKategoriler ? filtreleme(kategori.altKategoriler) : [];

        if (isimUyuyor) {
          // İsim uyuyorsa kategoriyi ve tüm alt kategorilerini göster
          acc.push({ ...kategori, altKategoriler: kategori.altKategoriler || [] });
        } else if (altKategorilerSonuc.length > 0) {
          // Yalnız bir alt kategori uyuyorsa, kategoriyi ve uyan dalları göster
          acc.push({ ...kategori, altKategoriler: altKategorilerSonuc });
        }
        return acc;
      }, []);
    };

    return filtreleme(kategoriler);
  }, [kategoriler, aramaMetni]);

  // "Kategorisiz" ağacın altında ayrı bir yaprak düğüm olarak gösterilir.
  const kategorisizSecili = secilenKategoriler.includes('Kategorisiz');
  const [kategorisizSayisi] = sablonSayilari.get('Kategorisiz') || [0, 0];
  // Arama varken yalnız "kategorisiz" sözcüğü eşleştiğinde göster.
  const kategorisizGoster = !aramaMetni || turkishToLower('Kategorisiz').includes(turkishToLower(aramaMetni));

  return (
    <div>
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Kategori ara..."
          value={aramaMetni}
          onChange={(e) => setAramaMetni(e.target.value)}
          className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
      </div>
      <div className="mb-1 flex items-center justify-between px-2">
        <div className="flex items-center gap-3 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1" title="Kategoriye doğrudan atanmış şablon sayısı">
            <DocumentTextIcon className="h-3 w-3" /> doğrudan
          </span>
          <span className="inline-flex items-center gap-1" title="Alt kategoriler dâhil toplam şablon sayısı">
            <RectangleStackIcon className="h-3 w-3 text-amber-500" /> alt kategorilerle
          </span>
        </div>
        {secilenKategoriler.length > 0 && (
          <button
            type="button"
            onClick={() => setSecilenKategoriler([])}
            className="shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-amber-600"
            title="Kategori filtresini temizle"
          >
            Filtreyi temizle
          </button>
        )}
      </div>
      <div>
        {filtrelenmiKategoriler.map((kat, index) => (
          <div key={`main-${kat.id || index}-${kat.isim}`}>
            {renderKategori(kat, kat.altKategoriler || [], 0, [])}
          </div>
        ))}

        {kategorisizGoster && (
          <>
            {filtrelenmiKategoriler.length > 0 && (
              <div className="my-2 border-t border-dashed border-slate-200" />
            )}
            <div className="group flex items-center rounded-lg p-2 transition-colors hover:bg-slate-100">
              <input
                type="checkbox"
                checked={kategorisizSecili}
                onChange={() => handleKategoriSecim('Kategorisiz', [])}
                aria-label="Kategorisiz şablonları filtrele"
                className="form-checkbox mr-2 h-5 w-5 rounded text-amber-500 focus:ring-amber-400"
              />
              {/* Yaprak filtre düğümü: genişlet/daralt yok, bu yüzden satır gövdesine
                  tıklamak bilinçli olarak seçimi toggle eder. */}
              <div
                className={`flex min-w-0 flex-grow cursor-pointer items-center ${
                  kategorisizSecili ? 'font-medium text-amber-700' : 'text-slate-500'
                }`}
                onClick={() => handleKategoriSecim('Kategorisiz', [])}
              >
                <MinusIcon className="mr-1 h-5 w-5 shrink-0 text-slate-300" />
                <FolderIcon className="mr-2 h-5 w-5 shrink-0 text-slate-300" />
                <span className="min-w-0 flex-1 truncate text-sm italic" title="Hiçbir kategoriye atanmamış şablonlar">
                  Kategorisiz
                </span>
                {kategorisizSayisi > 0 && (
                  <span className="app-num ml-2 flex shrink-0 items-center gap-1 text-[11px] leading-none">
                    <span
                      className="inline-flex items-center gap-0.5 text-slate-500"
                      title={`Kategorisiz ${kategorisizSayisi} şablon`}
                    >
                      <DocumentTextIcon className="h-3 w-3 text-slate-400" />
                      {kategorisizSayisi}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={handleKokKategoriEkle}
        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600"
        title="Yeni üst düzey kategori ekle"
      >
        <PlusIcon className="h-4 w-4" />
        Yeni kategori
      </button>
      <YeniKategoriModal
        isOpen={modalAcik}
        onClose={() => {
          setModalAcik(false);
          setDuzenlenecekKategori(null);
          setYeniKategoriParent(null);
          setParentId(null);
        }}
        onKategoriEkle={handleKategoriKaydet}
        parentKategori={yeniKategoriParent ? yeniKategoriParent.isim : null}
        parentId={parentId}
        duzenlenecekKategori={duzenlenecekKategori}
      />
      {contextMenu && (
        <KategoriContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          kategori={contextMenu.kategori}
          onClose={closeContextMenu}
          onDuzenle={handleKategoriDuzenle}
          onSil={handleKategoriSil}
          onYeniEkle={() => handleAltKategoriEkle(contextMenu.kategori)}
        />
      )}
    </div>
  );
};

export default KategoriAgaci;
